/**
 * Ship Progression Panel
 * Displays ship level, XP, specialization, perks, and stat bonuses
 */

import { EventBus } from '../../utils/EventBus';
import type { ShipProgression } from '../../managers/MMOProgressionManager';

export class ShipProgressionPanel {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private visible: boolean = false;
  private currentUnitId: string | null = null;

  // UI elements
  private levelText!: Phaser.GameObjects.Text;
  private xpBarBg!: Phaser.GameObjects.Rectangle;
  private xpBarFill!: Phaser.GameObjects.Rectangle;
  private xpText!: Phaser.GameObjects.Text;
  private specializationText!: Phaser.GameObjects.Text;
  private perksContainer!: Phaser.GameObjects.Container;
  private statsText!: Phaser.GameObjects.Text;
  private closeButton!: Phaser.GameObjects.Text;

  // Specialization buttons
  private specializationButtons: Map<string, Phaser.GameObjects.Container> = new Map();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Center of screen
    const centerX = scene.scale.width / 2;
    const centerY = scene.scale.height / 2;

    this.container = scene.add.container(centerX, centerY);
    this.container.setDepth(2000);
    this.container.setVisible(false);

    this.createPanel();
    this.setupEventListeners();
  }

  private createPanel(): void {
    const panelWidth = 652;
    const panelHeight = 500;

    // Semi-transparent background overlay
    const overlay = this.scene.add.rectangle(0, 0, this.scene.scale.width, this.scene.scale.height, 0x000000, 0.5);
    overlay.setInteractive();
    overlay.on('pointerdown', () => this.hide());
    this.container.add(overlay);

    // Main panel background
    const bg = this.scene.add.rectangle(0, 0, panelWidth, panelHeight, 0x1a1a2e, 0.98);
    bg.setStrokeStyle(3, 0x4a5568);
    this.container.add(bg);

    // Title
    const title = this.scene.add.text(0, -panelHeight / 2 + 25, 'Ship Progression', {
      fontSize: '20px',
      fontFamily: 'Arial',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    this.container.add(title);

    // Close button
    this.closeButton = this.scene.add.text(panelWidth / 2 - 25, -panelHeight / 2 + 25, '✕', {
      fontSize: '24px',
      color: '#ff6b6b',
    });
    this.closeButton.setOrigin(0.5);
    this.closeButton.setInteractive({ useHandCursor: true });
    this.closeButton.on('pointerdown', () => this.hide());
    this.closeButton.on('pointerover', () => this.closeButton.setColor('#ff0000'));
    this.closeButton.on('pointerout', () => this.closeButton.setColor('#ff6b6b'));
    this.container.add(this.closeButton);

    // Level display
    this.levelText = this.scene.add.text(0, -panelHeight / 2 + 70, 'Level 1', {
      fontSize: '24px',
      fontFamily: 'Arial',
      color: '#FFD700',
      fontStyle: 'bold',
    });
    this.levelText.setOrigin(0.5);
    this.container.add(this.levelText);

    // XP Bar
    const xpBarY = -panelHeight / 2 + 110;
    const xpBarWidth = panelWidth - 60;

    this.xpBarBg = this.scene.add.rectangle(0, xpBarY, xpBarWidth, 20, 0x2d3748);
    this.xpBarBg.setStrokeStyle(1, 0x4a5568);
    this.container.add(this.xpBarBg);

    this.xpBarFill = this.scene.add.rectangle(-xpBarWidth / 2, xpBarY, 0, 18, 0x4299e1);
    this.xpBarFill.setOrigin(0, 0.5);
    this.container.add(this.xpBarFill);

    this.xpText = this.scene.add.text(0, xpBarY, '0 / 100 XP', {
      fontSize: '12px',
      color: '#ffffff',
    });
    this.xpText.setOrigin(0.5);
    this.container.add(this.xpText);

    // Specialization section
    const specY = -panelHeight / 2 + 160;
    const specLabel = this.scene.add.text(-panelWidth / 2 + 30, specY, 'Specialization:', {
      fontSize: '14px',
      color: '#cccccc',
    });
    this.container.add(specLabel);

    this.specializationText = this.scene.add.text(-panelWidth / 2 + 30, specY + 25, 'None (Choose at Level 10)', {
      fontSize: '14px',
      color: '#FFD700',
      fontStyle: 'bold',
    });
    this.container.add(this.specializationText);

    // Specialization buttons (shown at level 10 if no specialization)
    this.createSpecializationButtons(specY + 50);

    // Perks section
    const perksY = -panelHeight / 2 + 280;
    const perksLabel = this.scene.add.text(-panelWidth / 2 + 30, perksY, 'Perks:', {
      fontSize: '14px',
      color: '#cccccc',
    });
    this.container.add(perksLabel);

    this.perksContainer = this.scene.add.container(0, perksY + 30);
    this.container.add(this.perksContainer);

    // Stats section
    const statsY = panelHeight / 2 - 100;
    const statsLabel = this.scene.add.text(-panelWidth / 2 + 30, statsY, 'Stat Bonuses:', {
      fontSize: '14px',
      color: '#cccccc',
    });
    this.container.add(statsLabel);

    this.statsText = this.scene.add.text(-panelWidth / 2 + 30, statsY + 25, '', {
      fontSize: '12px',
      color: '#4ade80',
      lineSpacing: 5,
    });
    this.container.add(this.statsText);
  }

  private createSpecializationButtons(y: number): void {
    const specializations = [
      { key: 'offensive', label: 'Offensive', icon: '⚔️', desc: 'Critical strikes, damage' },
      { key: 'defensive', label: 'Defensive', icon: '🛡️', desc: 'Shields, armor, reduction' },
      { key: 'tactical', label: 'Tactical', icon: '🎯', desc: 'Speed, evasion, sensors' },
      { key: 'support', label: 'Support', icon: '💚', desc: 'Team bonuses, economy' },
    ];

    const buttonWidth = 90;
    const buttonSpacing = 10;
    const startX = -(specializations.length * (buttonWidth + buttonSpacing)) / 2 + buttonWidth / 2;

    specializations.forEach((spec, index) => {
      const x = startX + index * (buttonWidth + buttonSpacing);
      const btnContainer = this.scene.add.container(x, y);

      const btn = this.scene.add.rectangle(0, 0, buttonWidth, 50, 0x2d3748);
      btn.setStrokeStyle(2, 0x4a5568);
      btn.setInteractive({ useHandCursor: true });

      const icon = this.scene.add.text(0, -10, spec.icon, {
        fontSize: '18px',
      });
      icon.setOrigin(0.5);

      const label = this.scene.add.text(0, 12, spec.label, {
        fontSize: '10px',
        color: '#ffffff',
      });
      label.setOrigin(0.5);

      btn.on('pointerover', () => {
        btn.setFillStyle(0x3d4758);
        btn.setStrokeStyle(2, 0x4299e1);
      });

      btn.on('pointerout', () => {
        btn.setFillStyle(0x2d3748);
        btn.setStrokeStyle(2, 0x4a5568);
      });

      btn.on('pointerdown', () => {
        if (this.currentUnitId) {
          EventBus.emit('set_specialization', { unitId: this.currentUnitId, specialization: spec.key });
          this.hideSpecializationButtons();
        }
      });

      btnContainer.add([btn, icon, label]);
      btnContainer.setVisible(false);
      this.container.add(btnContainer);
      this.specializationButtons.set(spec.key, btnContainer);
    });
  }

  private hideSpecializationButtons(): void {
    this.specializationButtons.forEach((btn) => btn.setVisible(false));
  }

  private showSpecializationButtons(): void {
    this.specializationButtons.forEach((btn) => btn.setVisible(true));
  }

  private setupEventListeners(): void {
    EventBus.on('ship_progression_updated', this.updateProgression, this);
    EventBus.on('ship_specialization_updated', this.onSpecializationSet, this);
  }

  private updateProgression(data: ShipProgression): void {
    if (this.currentUnitId !== data.unitId) return;

    // Update level
    this.levelText.setText(`Level ${data.level}${data.prestigeLevel > 0 ? ` (P${data.prestigeLevel})` : ''}`);

    // Update XP bar
    const xpPercent = data.experienceToNext > 0 ? data.experience / data.experienceToNext : 1;
    const xpBarWidth = (this.xpBarBg.width - 4) * Math.min(xpPercent, 1);
    this.xpBarFill.width = xpBarWidth;
    this.xpText.setText(`${data.experience} / ${data.experienceToNext} XP`);

    // Update specialization
    if (data.specialization) {
      this.specializationText.setText(this.capitalize(data.specialization));
      this.hideSpecializationButtons();
    } else if (data.level >= 10) {
      this.specializationText.setText('Choose your specialization:');
      this.showSpecializationButtons();
    } else {
      this.specializationText.setText(`None (Choose at Level 10)`);
      this.hideSpecializationButtons();
    }

    // Update perks
    this.updatePerks(data.perks);

    // Update stats
    this.updateStats(data.statBonuses);
  }

  private updatePerks(perks: string[]): void {
    this.perksContainer.removeAll(true);

    if (perks.length === 0) {
      const noneText = this.scene.add.text(0, 0, 'No perks yet (Earned at milestone levels)', {
        fontSize: '12px',
        color: '#888888',
      });
      noneText.setOrigin(0.5, 0);
      this.perksContainer.add(noneText);
      return;
    }

    perks.forEach((perk, index) => {
      const perkText = this.scene.add.text(-150, index * 20, `• ${perk}`, {
        fontSize: '12px',
        color: '#4ade80',
      });
      this.perksContainer.add(perkText);
    });
  }

  private updateStats(bonuses: {
    hpBonus: number;
    attackBonus: number;
    defenseBonus: number;
    speedBonus: number;
  }): void {
    const statsLines = [
      `HP: +${bonuses.hpBonus.toFixed(1)}%`,
      `Attack: +${bonuses.attackBonus.toFixed(1)}%`,
      `Defense: +${bonuses.defenseBonus.toFixed(1)}%`,
      `Speed: +${bonuses.speedBonus.toFixed(1)}%`,
    ];

    this.statsText.setText(statsLines.join('\n'));
  }

  private onSpecializationSet(data: { success: boolean; unitId: string; specialization: string }): void {
    if (data.success && data.unitId === this.currentUnitId) {
      this.hideSpecializationButtons();
      this.specializationText.setText(this.capitalize(data.specialization));
    }
  }

  private capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  public show(unitId: string): void {
    this.currentUnitId = unitId;
    this.visible = true;
    this.container.setVisible(true);

    // Request ship progression from server
    EventBus.emit('request_ship_progression', { unitId });

    // Fade in animation
    this.container.setAlpha(0);
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: 200,
      ease: 'Power2',
    });

    EventBus.on('close-topmost-dialog', this.hide, this);
  }

  public hide(): void {
    EventBus.off('close-topmost-dialog', this.hide, this);
    this.visible = false;
    this.currentUnitId = null;

    // Fade out animation
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: 200,
      ease: 'Power2',
      onComplete: () => {
        this.container.setVisible(false);
      },
    });
  }

  public isVisible(): boolean {
    return this.visible;
  }

  public destroy(): void {
    EventBus.off('ship_progression_updated', this.updateProgression, this);
    EventBus.off('ship_specialization_updated', this.onSpecializationSet, this);
    this.specializationButtons.clear();
    this.container.destroy();
  }
}
